import { useState, useEffect } from "react";
import { doc, getDoc } from "firebase/firestore";
import { signInWithPopup, GoogleAuthProvider } from "firebase/auth";
import { httpsCallable } from "firebase/functions";
import { db, auth, functions } from "../firebase";

const FONT = "'Plus Jakarta Sans', sans-serif";

function Isotipo({ size = 36 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 40 40">
      <line x1="20" y1="5" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="35" y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="20" y1="35" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <line x1="5" y1="20" x2="20" y2="20" stroke="#D1D5DB" strokeWidth="2" />
      <circle cx="20" cy="5" r="3" fill="#E03131" />
      <circle cx="35" cy="20" r="3" fill="#E03131" />
      <circle cx="20" cy="35" r="3" fill="#E03131" />
      <circle cx="5" cy="20" r="3" fill="#E03131" />
      <circle cx="20" cy="20" r="6" fill="#E03131" />
    </svg>
  );
}

function StatusIcon({ type }) {
  const icons = {
    invalid: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#dc2626"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M15 9l-6 6" />
        <path d="M9 9l6 6" />
      </svg>
    ),
    expired: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#f59e0b"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M12 6v6l4 2" />
      </svg>
    ),
    used: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#22c55e"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <circle cx="12" cy="12" r="10" />
        <path d="M9 12l2 2 4-4" />
      </svg>
    ),
    ready: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#E03131"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
        <circle cx="9" cy="7" r="4" />
        <path d="M22 21v-2a4 4 0 0 0-3-3.87" />
        <path d="M16 3.13a4 4 0 0 1 0 7.75" />
      </svg>
    ),
    success: (
      <svg
        width="48"
        height="48"
        viewBox="0 0 24 24"
        fill="none"
        stroke="#22c55e"
        strokeWidth="2"
        strokeLinecap="round"
      >
        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
        <path d="M22 4L12 14.01l-3-3" />
      </svg>
    ),
  };
  return <div style={{ marginBottom: 16 }}>{icons[type]}</div>;
}

export default function InvitationPage() {
  const [status, setStatus] = useState("loading");
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState("");

  const token = new URLSearchParams(window.location.search).get("token");

  useEffect(() => {
    if (!token) {
      setStatus("invalid");
      return;
    }
    async function loadInvitation() {
      try {
        const invDoc = await getDoc(doc(db, "invitations", token));
        if (!invDoc.exists()) {
          setStatus("invalid");
          return;
        }
        const inv = invDoc.data();
        if (inv.status === "accepted") {
          setStatus("already_used");
          return;
        }
        if (new Date(inv.expires_at) < new Date()) {
          setStatus("expired");
          return;
        }
        setInvitation(inv);
        setStatus("ready");
      } catch (e) {
        setStatus("invalid");
      }
    }
    loadInvitation();
  }, [token]);

  async function handleAccept() {
    setStatus("accepting");
    try {
      const provider = new GoogleAuthProvider();
      provider.setCustomParameters({ login_hint: invitation.email });
      const result = await signInWithPopup(auth, provider);
      const googleEmail = result.user.email.toLowerCase();
      const invEmail = invitation.email.toLowerCase();

      if (googleEmail !== invEmail) {
        await auth.signOut();
        setError(
          `Debés ingresar con ${invitation.email}, no con ${googleEmail}`,
        );
        setStatus("ready");
        return;
      }

      const acceptInvitationFn = httpsCallable(functions, "acceptInvitation");
      await acceptInvitationFn({ token });

      setStatus("success");
      setTimeout(() => {
        window.location.href = "/";
      }, 2000);
    } catch (e) {
      if (e.code !== "auth/popup-closed-by-user") {
        setError(e.message || "Error al activar la cuenta.");
      }
      setStatus("ready");
    }
  }

  return (
    <div style={sx.container}>
      <div style={sx.card}>
        <div style={sx.logoArea}>
          <Isotipo size={36} />
          <div style={sx.logoText}>
            <span style={{ color: "#0B0F14", fontWeight: 500 }}>Obra</span>
            <span style={{ color: "#E03131", fontWeight: 700 }}>Core</span>
          </div>
        </div>

        {status === "loading" && (
          <p style={sx.text}>Verificando invitación...</p>
        )}

        {status === "invalid" && (
          <>
            <StatusIcon type="invalid" />
            <h2 style={sx.title}>Invitación inválida</h2>
            <p style={sx.text}>Este link no existe o ya fue usado.</p>
          </>
        )}

        {status === "expired" && (
          <>
            <StatusIcon type="expired" />
            <h2 style={sx.title}>Invitación expirada</h2>
            <p style={sx.text}>
              Este link venció. Pedile al administrador que te envíe uno nuevo.
            </p>
          </>
        )}

        {status === "already_used" && (
          <>
            <StatusIcon type="used" />
            <h2 style={sx.title}>Ya activaste tu cuenta</h2>
            <p style={sx.text}>Podés ingresar directamente.</p>
            <button style={sx.btn} onClick={() => (window.location.href = "/")}>
              Ir a ObraCore
            </button>
          </>
        )}

        {status === "ready" && invitation && (
          <>
            <StatusIcon type="ready" />
            <h2 style={sx.title}>Te invitaron a ObraCore</h2>
            <p style={sx.text}>
              Fuiste invitado como parte de{" "}
              <strong>{invitation.tenant_id}</strong>.
            </p>
            <div style={sx.emailHint}>
              Ingresá con: <strong>{invitation.email}</strong>
            </div>
            {error && <div style={sx.error}>{error}</div>}
            <button style={sx.btn} onClick={handleAccept}>
              <svg
                width="18"
                height="18"
                viewBox="0 0 24 24"
                style={{ marginRight: 8 }}
              >
                <path
                  d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                  fill="#fff"
                  fillOpacity="0.8"
                />
                <path
                  d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
                  fill="#fff"
                  fillOpacity="0.8"
                />
                <path
                  d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
                  fill="#fff"
                  fillOpacity="0.8"
                />
                <path
                  d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                  fill="#fff"
                  fillOpacity="0.8"
                />
              </svg>
              Activar mi cuenta con Google
            </button>
          </>
        )}

        {status === "accepting" && (
          <>
            <div style={{ marginBottom: 16 }}>
              <svg
                width="36"
                height="36"
                viewBox="0 0 24 24"
                fill="none"
                stroke="#E03131"
                strokeWidth="2"
                strokeLinecap="round"
                style={{ animation: "spin 1s linear infinite" }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
            </div>
            <p style={sx.text}>Activando tu cuenta...</p>
            <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
          </>
        )}

        {status === "success" && (
          <>
            <StatusIcon type="success" />
            <h2 style={sx.title}>Cuenta activada</h2>
            <p style={sx.text}>Redirigiendo al dashboard...</p>
          </>
        )}

        <div style={sx.footer}>
          <span style={{ fontSize: 11, color: "#94a3b8" }}>
            Plataforma de gestión integral
          </span>
        </div>
      </div>
    </div>
  );
}

const sx = {
  container: {
    minHeight: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background: "#f8f9fa",
    fontFamily: FONT,
  },
  card: {
    background: "white",
    borderRadius: 16,
    padding: "48px 40px",
    width: 420,
    boxShadow: "0 4px 24px rgba(0,0,0,0.08)",
    textAlign: "center",
    borderTop: "3px solid #E03131",
  },
  logoArea: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    gap: 10,
    marginBottom: 24,
  },
  logoText: { fontSize: 24, letterSpacing: -0.5 },
  title: { fontSize: 20, fontWeight: 700, color: "#1e293b", marginBottom: 8 },
  text: { fontSize: 14, color: "#64748b", lineHeight: 1.6, marginBottom: 16 },
  emailHint: {
    fontSize: 13,
    color: "#475569",
    background: "#f8fafc",
    padding: "10px 16px",
    borderRadius: 8,
    marginBottom: 20,
    border: "1px solid #f1f5f9",
  },
  error: {
    fontSize: 13,
    color: "#dc2626",
    background: "#fef2f2",
    padding: "10px 16px",
    borderRadius: 8,
    marginBottom: 16,
    border: "1px solid #fecaca",
  },
  btn: {
    width: "100%",
    padding: "12px",
    background: "#E03131",
    color: "white",
    border: "none",
    borderRadius: 8,
    fontSize: 15,
    fontWeight: 700,
    cursor: "pointer",
    fontFamily: FONT,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    transition: "opacity 0.15s",
  },
  footer: {
    marginTop: 24,
    paddingTop: 16,
    borderTop: "1px solid #f1f5f9",
  },
};
